/**
 * Environment Configuration Validation
 * 
 * This module provides runtime validation for environment variables
 * to ensure the application has all required configuration before startup.
 */

export interface EnvironmentConfig {
  // Required Supabase configuration
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_PUBLISHABLE_KEY: string;
  VITE_SUPABASE_PROJECT_ID: string;
  
  // Optional configuration
  VITE_SUPABASE_TEST_URL?: string;
  VITE_SUPABASE_TEST_KEY?: string;
  ANALYZE?: string;
  SECURITY_THRESHOLD_CRITICAL?: string;
  SECURITY_THRESHOLD_HIGH?: string;
  SECURITY_THRESHOLD_MODERATE?: string;
}

export interface EnvironmentValidationResult {
  isValid: boolean;
  config: Partial<EnvironmentConfig>;
  missingRequired: string[];
  missingOptional: string[];
  errors: string[];
  warnings: string[];
}

/**
 * List of required environment variables
 */
const REQUIRED_VARIABLES: (keyof EnvironmentConfig)[] = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
  'VITE_SUPABASE_PROJECT_ID'
];

/**
 * List of optional environment variables
 */
const OPTIONAL_VARIABLES: (keyof EnvironmentConfig)[] = [
  'VITE_SUPABASE_TEST_URL',
  'VITE_SUPABASE_TEST_KEY',
  'ANALYZE',
  'SECURITY_THRESHOLD_CRITICAL',
  'SECURITY_THRESHOLD_HIGH',
  'SECURITY_THRESHOLD_MODERATE'
];

/**
 * Validation rules for environment variables
 */
const VALIDATION_RULES: Record<string, (value: string) => string | null> = {
  VITE_SUPABASE_URL: (value: string) => {
    try {
      const url = new URL(value);
      if (!url.hostname.includes('supabase.co')) {
        return 'VITE_SUPABASE_URL must be a valid Supabase URL (*.supabase.co)';
      }
      return null;
    } catch {
      return 'VITE_SUPABASE_URL must be a valid URL';
    }
  },
  
  VITE_SUPABASE_PUBLISHABLE_KEY: (value: string) => {
    if (!value.startsWith('eyJ')) {
      return 'VITE_SUPABASE_PUBLISHABLE_KEY must be a valid JWT token';
    }
    return null;
  },
  
  VITE_SUPABASE_PROJECT_ID: (value: string) => {
    if (!/^[a-z0-9]+$/.test(value)) {
      return 'VITE_SUPABASE_PROJECT_ID must contain only lowercase letters and numbers';
    }
    return null;
  },
  
  VITE_SUPABASE_TEST_URL: (value: string) => {
    try {
      const url = new URL(value);
      if (!url.hostname.includes('supabase.co')) {
        return 'VITE_SUPABASE_TEST_URL must be a valid Supabase URL (*.supabase.co)';
      }
      return null;
    } catch {
      return 'VITE_SUPABASE_TEST_URL must be a valid URL';
    }
  },
  
  VITE_SUPABASE_TEST_KEY: (value: string) => {
    if (!value.startsWith('eyJ')) {
      return 'VITE_SUPABASE_TEST_KEY must be a valid JWT token';
    }
    return null;
  },
  
  ANALYZE: (value: string) => {
    if (!['true', 'false'].includes(value.toLowerCase())) {
      return 'ANALYZE must be either "true" or "false"';
    }
    return null;
  },
  
  SECURITY_THRESHOLD_CRITICAL: (value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) {
      return 'SECURITY_THRESHOLD_CRITICAL must be a non-negative integer';
    }
    return null;
  },
  
  SECURITY_THRESHOLD_HIGH: (value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) {
      return 'SECURITY_THRESHOLD_HIGH must be a non-negative integer';
    }
    return null;
  },
  
  SECURITY_THRESHOLD_MODERATE: (value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) {
      return 'SECURITY_THRESHOLD_MODERATE must be a non-negative integer';
    }
    return null;
  }
};

/**
 * Environment Validator Class
 */
export class EnvironmentValidator {
  /**
   * Validate all environment variables
   */
  static validate(): EnvironmentValidationResult {
    const config: Partial<EnvironmentConfig> = {};
    const missingRequired: string[] = [];
    const missingOptional: string[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required variables
    for (const variable of REQUIRED_VARIABLES) {
      const value = import.meta.env[variable];
      
      if (!value) {
        missingRequired.push(variable);
        errors.push(`Missing required environment variable: ${variable}`);
      } else {
        config[variable] = value;
        
        // Validate format if rule exists
        const validator = VALIDATION_RULES[variable];
        if (validator) {
          const error = validator(value);
          if (error) {
            errors.push(error);
          }
        }
      }
    }

    // Check optional variables
    for (const variable of OPTIONAL_VARIABLES) {
      const value = import.meta.env[variable];
      
      if (!value) {
        missingOptional.push(variable);
      } else {
        config[variable] = value;
        
        // Validate format if rule exists
        const validator = VALIDATION_RULES[variable];
        if (validator) {
          const error = validator(value);
          if (error) {
            warnings.push(error);
          }
        }
      }
    }

    // Additional validation checks
    this.performCrossValidation(config, errors, warnings);

    const isValid = errors.length === 0 && missingRequired.length === 0;

    return {
      isValid,
      config,
      missingRequired,
      missingOptional,
      errors,
      warnings
    };
  }

  /**
   * Perform cross-validation between related environment variables
   */
  private static performCrossValidation(
    config: Partial<EnvironmentConfig>,
    errors: string[],
    warnings: string[]
  ): void {
    // Check if test variables are provided together
    const hasTestUrl = !!config.VITE_SUPABASE_TEST_URL;
    const hasTestKey = !!config.VITE_SUPABASE_TEST_KEY;
    
    if (hasTestUrl && !hasTestKey) {
      warnings.push('VITE_SUPABASE_TEST_URL is provided but VITE_SUPABASE_TEST_KEY is missing. Integration tests may not work properly.');
    }
    
    if (hasTestKey && !hasTestUrl) {
      warnings.push('VITE_SUPABASE_TEST_KEY is provided but VITE_SUPABASE_TEST_URL is missing. Integration tests may not work properly.');
    }

    // Check if project ID matches URL
    if (config.VITE_SUPABASE_URL && config.VITE_SUPABASE_PROJECT_ID) {
      try {
        const url = new URL(config.VITE_SUPABASE_URL);
        const urlProjectId = url.hostname.split('.')[0];
        
        if (urlProjectId !== config.VITE_SUPABASE_PROJECT_ID) {
          errors.push('VITE_SUPABASE_PROJECT_ID does not match the project ID in VITE_SUPABASE_URL');
        }
      } catch {
        // URL validation will catch this
      }
    }
  }

  /**
   * Get list of required environment variables
   */
  static getRequiredVariables(): string[] {
    return [...REQUIRED_VARIABLES];
  }

  /**
   * Get list of optional environment variables
   */
  static getOptionalVariables(): string[] {
    return [...OPTIONAL_VARIABLES];
  }

  /**
   * Get all environment variables (required + optional)
   */
  static getAllVariables(): string[] {
    return [...REQUIRED_VARIABLES, ...OPTIONAL_VARIABLES];
  }

  /**
   * Check if integration tests can be run
   */
  static canRunIntegrationTests(): boolean {
    const testUrl = import.meta.env.VITE_SUPABASE_TEST_URL;
    const testKey = import.meta.env.VITE_SUPABASE_TEST_KEY;
    return !!(testUrl && testKey);
  }

  /**
   * Get a formatted error message for missing configuration
   */
  static getSetupInstructions(result: EnvironmentValidationResult): string {
    if (result.isValid) {
      return 'Environment configuration is valid.';
    }

    const instructions: string[] = [
      '🔧 Environment Configuration Issues Detected',
      '',
      'Please follow these steps to fix your configuration:',
      ''
    ];

    if (result.missingRequired.length > 0) {
      instructions.push('❌ Missing Required Variables:');
      result.missingRequired.forEach(variable => {
        instructions.push(`   • ${variable}`);
      });
      instructions.push('');
    }

    if (result.errors.length > 0) {
      instructions.push('❌ Configuration Errors:');
      result.errors.forEach(error => {
        instructions.push(`   • ${error}`);
      });
      instructions.push('');
    }

    if (result.warnings.length > 0) {
      instructions.push('⚠️  Configuration Warnings:');
      result.warnings.forEach(warning => {
        instructions.push(`   • ${warning}`);
      });
      instructions.push('');
    }

    instructions.push(
      '📋 Setup Instructions:',
      '',
      '1. Copy .env.example to .env:',
      '   cp .env.example .env',
      '',
      '2. Edit .env and fill in your Supabase project details:',
      '   - Get your project URL and keys from https://supabase.com/dashboard',
      '   - Navigate to Settings > API in your Supabase project',
      '   - Copy the Project URL and anon/public key',
      '',
      '3. Restart your development server:',
      '   npm run dev',
      '',
      '📚 For more help, see the .env.example file for detailed variable descriptions.'
    );

    return instructions.join('\n');
  }
}

/**
 * Convenience function to validate environment and throw if invalid
 */
export function validateEnvironmentOrThrow(): EnvironmentConfig {
  const result = EnvironmentValidator.validate();
  
  if (!result.isValid) {
    const instructions = EnvironmentValidator.getSetupInstructions(result);
    throw new Error(`Environment validation failed:\n\n${instructions}`);
  }
  
  return result.config as EnvironmentConfig;
}

/**
 * Convenience function to validate environment and return result
 */
export function validateEnvironment(): EnvironmentValidationResult {
  return EnvironmentValidator.validate();
}